import React, { useState } from "react";
import {
  getRiskLevel,
  calculateSectionBScore,
  ADEQUACY_CUTOFF,
  MAX_SCORE
} from "./vitaminDCalculations";
import { formTranslations } from "./FormTranslations";

const API_BASE = "https://backend-coupon-distribution-nhf.onrender.com";

// Coupon shown on screen after the user submits their phone number
const COUPON_CODE = "PULSED";
const REDEEM_LINK =
  "https://www.1mg.com/otc/deksel-oral-nano-liquid-60000-iu-5ml-each-sugar-free-otc634337";

const EMPTY_FORM = {
  age: "",
  q5: "",
  q6: "",
  q7: "",
  q9: "",
  q12: "",
  q13: "",
  q14: "",
  q15: "",
  q16: "",
  q17: "",
  q18: "",
};

// Questions shown in Section B (q1 is derived from age)
const QUESTION_NUMBERS = [5, 6, 7, 9, 12, 13, 14, 15, 16, 17, 18];

// Visual zones for the result bar (green / yellow / red)
const GREEN_END = ADEQUACY_CUTOFF; // 0 - 3   Adequate
const YELLOW_END = 6;              // 3 - 6   Inadequate (moderate)
                                   // 6 - 11  Inadequate (high)

const StandaloneQuestionnaire = () => {
  const translations = formTranslations.english;
  const [errorField, setErrorField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { score, riskLevel }
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [phone, setPhone] = useState("");
  const [isSendingCoupon, setIsSendingCoupon] = useState(false);
  const [couponSent, setCouponSent] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [codeCopied, setCodeCopied] = useState(false);

  const scrollToField = (key) => {
    const el = document.getElementById(`group-${key}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleCalculateScore = async () => {
    if (isSubmitting) return;

    // 1. Age is mandatory
    if (!formData.age.toString().trim()) {
      setErrorField("age");
      scrollToField("age");
      return;
    }

    // 2. Age range check
    const age = Number(formData.age);
    if (age <= 0 || age >= 120) {
      alert("Enter valid age");
      setErrorField("age");
      scrollToField("age");
      return;
    }

    // 3. Validate questionnaire questions
    for (const num of QUESTION_NUMBERS) {
      const qKey = `q${num}`;
      if (!formData[qKey]) {
        setErrorField(qKey);
        scrollToField(qKey);
        return;
      }
    }

    setErrorField(null);

    const totalScore = calculateSectionBScore(formData, age);
    const riskLevel = getRiskLevel(totalScore);

    setIsSubmitting(true);

    // Send only the d3 score to the backend
    try {
      const response = await fetch(`${API_BASE}/save-d3score`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ d3score: totalScore }),
      });
      const data = await response.json();
      console.log("save-d3score response:", data);
      if (!response.ok) {
        console.error("Failed to save d3score:", data.error || data);
      }
    } catch (err) {
      // Saving is not critical for the user; still show their result
      console.error("Error calling save-d3score:", err);
    }

    setIsSubmitting(false);
    setResult({ score: totalScore, riskLevel });
    window.scrollTo(0, 0);
  };

  const handleReset = () => {
    setFormData(EMPTY_FORM);
    setErrorField(null);
  };

  const handleStartOver = () => {
    setResult(null);
    setPhone("");
    setCouponSent(false);
    setCouponError("");
    setCodeCopied(false);
    handleReset();
    window.scrollTo(0, 0);
  };

  const handleSendCoupon = async () => {
    if (isSendingCoupon || couponSent || phone.length !== 10) return;

    setIsSendingCoupon(true);
    setCouponError("");

    try {
      const response = await fetch(`${API_BASE}/send-user-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: phone }),
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        setCouponError(data.error || "Could not send the message. Please try again.");
      } else {
        setCouponSent(true);
      }
    } catch (err) {
      console.error("Error calling send-user-report:", err);
      setCouponError("Could not send the message. Please check your connection and try again.");
    }

    setIsSendingCoupon(false);
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
    } catch (err) {
      // Fallback for browsers that block the clipboard API
      const textarea = document.createElement("textarea");
      textarea.value = COUPON_CODE;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
      } catch (e) {
        console.error("Copy failed:", e);
      }
      document.body.removeChild(textarea);
    }
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const styles = `
    .sq-root { font-family: 'Inter', Arial, sans-serif; background: #f8fafc; min-height: 100vh; padding: 20px; color: #0f172a; }
    .container { max-width: 800px; margin: 0 auto; background: white; border-radius: 16px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); padding: 30px; }
    .header { text-align: center; margin-bottom: 30px; }
    .header h1 { font-size: 24px; color: #10b981; margin-bottom: 8px; }
    .header p { color: #64748b; font-size: 14px; }
    .section { margin-bottom: 30px; }
    .section-title { font-weight: 700; font-size: 16px; color: #10b981; margin-bottom: 20px; padding-bottom: 8px; border-bottom: 2px solid #ecfdf5; display: flex; align-items: center; gap: 8px; }
    .form-group { margin-bottom: 20px; }
    label { display: block; margin-bottom: 6px; font-weight: 600; font-size: 13px; color: #475569; text-transform: uppercase; letter-spacing: 0.025em; }
    input[type="text"], input[type="number"], select { width: 100%; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px; transition: all 0.2s; background: #f8fafc; box-sizing: border-box; }
    input:focus, select:focus { border-color: #10b981; outline: none; background: white; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1); }
    .button-group { display: flex; gap: 12px; margin-top: 40px; justify-content: flex-end; }
    button { padding: 12px 24px; font-size: 14px; font-weight: 600; border-radius: 8px; cursor: pointer; transition: all 0.2s; border: none; }
    .btn-calculate { background: #10b981; color: white; flex: 1; }
    .btn-calculate:hover { background: #059669; transform: translateY(-1px); }
    .btn-calculate:disabled { background: #94a3b8; cursor: not-allowed; transform: none; }
    .btn-reset { background: #f1f5f9; color: #475569; }
    .btn-reset:hover { background: #e2e8f0; }
    .btn-reset:disabled { opacity: 0.6; cursor: not-allowed; }

    .has-error input, .has-error select { border-color: #ef4444 !important; background-color: #fef2f2 !important; box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important; }
    .error-msg { color: #ef4444; font-size: 11px; font-weight: 600; margin-top: 4px; display: block; }
    .has-error label { color: #ef4444 !important; }

    /* Result */
    .result-card { text-align: center; }
    .result-heading { font-size: 14px; font-weight: 600; color: #64748b; margin-bottom: 8px; }
    .result-score { font-size: 44px; font-weight: 800; line-height: 1; margin-bottom: 4px; }
    .result-score small { font-size: 18px; font-weight: 600; color: #94a3b8; }
    .result-level { font-size: 20px; font-weight: 800; margin-bottom: 24px; }
    .result-level.good { color: #059669; }
    .result-level.mid { color: #d97706; }
    .result-level.bad { color: #dc2626; }

    .bar-wrap { position: relative; padding-top: 14px; margin: 0 4px 8px; }
    .bar { display: flex; height: 16px; border-radius: 999px; overflow: hidden; }
    .bar-seg.green { background: #22c55e; }
    .bar-seg.yellow { background: #facc15; }
    .bar-seg.red { background: #ef4444; }
    .bar-marker { position: absolute; top: 0; transform: translateX(-50%); width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 11px solid #0f172a; transition: left 0.6s ease-out; }
    .bar-labels { display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; margin: 0 4px 24px; }
    .bar-legend { display: flex; justify-content: center; flex-wrap: wrap; gap: 14px; font-size: 12px; color: #475569; margin-bottom: 20px; }
    .bar-legend span { display: inline-flex; align-items: center; gap: 6px; }
    .dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
    .dot.green { background: #22c55e; }
    .dot.yellow { background: #facc15; }
    .dot.red { background: #ef4444; }

    .result-text { color: #475569; font-size: 15px; line-height: 1.6; margin-bottom: 12px; }
    .result-disclaimer { font-size: 12px; color: #94a3b8; line-height: 1.5; }

    .coupon-box { margin-top: 32px; padding: 24px; border: 1px solid #d1fae5; background: #f0fdf4; border-radius: 12px; text-align: left; }
    .coupon-title { font-size: 17px; font-weight: 800; color: #065f46; margin-bottom: 6px; }
    .coupon-text { font-size: 14px; color: #475569; margin-bottom: 16px; line-height: 1.5; }
    .phone-input-group { display: flex; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; background: white; margin-bottom: 12px; }
    .phone-input-group:focus-within { border-color: #10b981; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1); }
    .phone-prefix { padding: 12px 14px; background: #f1f5f9; color: #475569; font-size: 14px; font-weight: 600; border-right: 1px solid #e2e8f0; }
    .phone-input-group input { border: none !important; border-radius: 0 !important; background: transparent !important; box-shadow: none !important; flex: 1; }
    .btn-send { background: #10b981; color: white; width: 100%; padding: 14px; font-size: 15px; }
    .btn-send:hover:not(:disabled) { background: #059669; }
    .btn-send:disabled { background: #94a3b8; cursor: not-allowed; }
    .coupon-msg { font-size: 13px; margin-top: 12px; line-height: 1.4; }
    .coupon-msg.ok { color: #047857; font-weight: 600; }
    .coupon-msg.err { color: #dc2626; font-weight: 600; }
    .btn-link { background: none; color: #64748b; margin-top: 20px; text-decoration: underline; padding: 8px; }

    /* On-screen coupon reveal */
    .coupon-reveal { margin-top: 18px; padding-top: 18px; border-top: 1px dashed #a7f3d0; }
    .coupon-reveal-label { font-size: 12px; font-weight: 700; color: #065f46; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
    .coupon-code-row { display: flex; align-items: stretch; gap: 8px; margin-bottom: 14px; }
    .coupon-code { flex: 1; display: flex; align-items: center; justify-content: center; padding: 12px; background: white; border: 2px dashed #10b981; border-radius: 8px; font-size: 22px; font-weight: 800; letter-spacing: 0.15em; color: #065f46; }
    .btn-copy { background: #065f46; color: white; padding: 0 20px; min-width: 90px; }
    .btn-copy:hover { background: #047857; }
    .btn-redeem { display: block; width: 100%; box-sizing: border-box; text-align: center; background: #10b981; color: white; padding: 14px; font-size: 15px; font-weight: 600; border-radius: 8px; text-decoration: none; transition: all 0.2s; }
    .btn-redeem:hover { background: #059669; }
  `;

  // ----- Result view -----
  if (result) {
    const { score, riskLevel } = result;
    const isAdequate = riskLevel === "Adequate";
    const zone = score < GREEN_END ? "good" : score < YELLOW_END ? "mid" : "bad";
    const levelLabel =
      zone === "good" ? "Adequate" : zone === "mid" ? "Inadequate (moderate)" : "Inadequate (high risk)";
    const markerPct = Math.min(100, Math.max(0, (score / MAX_SCORE) * 100));
    const pct = (n) => `${(n / MAX_SCORE) * 100}%`;

    return (
      <div className="sq-root">
        <style>{styles}</style>
        <div className="container">
          <div className="result-card">
            <p className="result-heading">Your vitamin D screening score</p>
            <div className="result-score">
              {score} <small>/ {MAX_SCORE}</small>
            </div>
            <div className={`result-level ${zone}`}>{levelLabel}</div>

            <div className="bar-wrap">
              <div className="bar-marker" style={{ left: `${markerPct}%` }} />
              <div className="bar">
                <div className="bar-seg green" style={{ width: pct(GREEN_END) }} />
                <div className="bar-seg yellow" style={{ width: pct(YELLOW_END - GREEN_END) }} />
                <div className="bar-seg red" style={{ width: pct(MAX_SCORE - YELLOW_END) }} />
              </div>
            </div>
            <div className="bar-labels">
              <span>0</span>
              <span>{MAX_SCORE}</span>
            </div>
            <div className="bar-legend">
              <span><i className="dot green" /> Adequate (below {GREEN_END})</span>
              <span><i className="dot yellow" /> Inadequate ({GREEN_END}–{YELLOW_END - 0.25})</span>
              <span><i className="dot red" /> Inadequate ({YELLOW_END}+)</span>
            </div>

            <p className="result-text">
              {isAdequate
                ? "Your answers suggest your vitamin D level is likely adequate."
                : "Your answers suggest your vitamin D level may be inadequate."}
            </p>
            <p className="result-disclaimer">
              This is a screening test and not a substitute for a diagnostic test.
              Please consult your doctor for interpretation and appropriate treatment.
            </p>
          </div>

          <div className="coupon-box">
            <div className="coupon-title">🎟️ Get your Vitamin D3 coupon code</div>
            <p className="coupon-text">
              Enter your phone number to receive your coupon code for the vitamin D3 test
              here on this page and on WhatsApp as well.
            </p>
            <div className="phone-input-group">
              <span className="phone-prefix">+91</span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={10}
                placeholder="Enter 10-digit number"
                value={phone}
                disabled={isSendingCoupon || couponSent}
                onChange={(e) => {
                  setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                  if (couponError) setCouponError("");
                }}
              />
            </div>
            <button
              className="btn-send"
              onClick={handleSendCoupon}
              disabled={phone.length !== 10 || isSendingCoupon || couponSent}
            >
              {isSendingCoupon ? "Sending..." : couponSent ? "Sent ✓" : "Send"}
            </button>
            {couponSent && (
              <p className="coupon-msg ok">
                Done! Please check WhatsApp on +91 {phone}.
              </p>
            )}
            {couponError && <p className="coupon-msg err">{couponError}</p>}

            {couponSent && (
              <div className="coupon-reveal">
                <div className="coupon-reveal-label">Your coupon code</div>
                <div className="coupon-code-row">
                  <div className="coupon-code">{COUPON_CODE}</div>
                  <button className="btn-copy" onClick={handleCopyCode}>
                    {codeCopied ? "Copied ✓" : "Copy"}
                  </button>
                </div>
                <a
                  className="btn-redeem"
                  href={REDEEM_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Redeem now
                </a>
              </div>
            )}
          </div>

          <div style={{ textAlign: "center" }}>
            <button className="btn-link" onClick={handleStartOver}>
              Retake the questionnaire
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ----- Form view -----
  return (
    <div className="sq-root">
      <style>{styles}</style>
      <div className="container">

        <div className="header">
          <h1>🏥 {translations.title}</h1>
          <p>Please answer every question as precisely as you can.</p>
        </div>

        {/* Section A */}
        <div className="section">
          <h2 className="section-title">👤 {translations.sectionA}</h2>

          <div className={`form-group ${errorField === "age" ? "has-error" : ""}`} id="group-age">
            <label>{translations.age} *</label>
            <input
              type="number"
              value={formData.age}
              onChange={(e) => {
                setFormData({ ...formData, age: e.target.value });
                if (errorField === "age") setErrorField(null);
              }}
            />
            {errorField === "age" && <span className="error-msg">Required</span>}
          </div>
        </div>

        {/* Section B */}
        <div className="section">
          <h2 className="section-title">📝 {translations.sectionB}</h2>

          {QUESTION_NUMBERS.map((num) => {
            const qKey = `q${num}`;
            const qData = translations.questions[qKey];
            const qText = typeof qData === "string" ? qData : qData.text;
            const qOptions = qData.options || [translations.yes, translations.no];

            // Option values (must match the order of the options in translations)
            let values = ["yes", "no"];
            if (num === 5) values = ["negligible", "less_30", "more_30"];
            if (num >= 13 && num <= 17) values = ["often", "sometimes", "no"];

            return (
              <div key={qKey} className={`form-group ${errorField === qKey ? "has-error" : ""}`} id={`group-${qKey}`}>
                <label className="q-label">{qText} *</label>
                <select
                  value={formData[qKey]}
                  onChange={(e) => {
                    setFormData({ ...formData, [qKey]: e.target.value });
                    if (errorField === qKey) setErrorField(null);
                  }}
                >
                  <option value="">{translations.select}</option>
                  {qOptions.map((opt, i) => (
                    <option key={i} value={values[i]}>{opt}</option>
                  ))}
                </select>
                {errorField === qKey && <span className="error-msg">Please select an option</span>}
              </div>
            );
          })}
        </div>

        <div className="button-group">
          <button className="btn-reset" onClick={handleReset} disabled={isSubmitting}>Reset Form</button>
          <button className="btn-calculate" onClick={handleCalculateScore} disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StandaloneQuestionnaire;
